import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchBrands, fetchBusinessSettings, kioskPay, kioskDefaultReader, createOrder } from "@/lib/api";
import { getKioskReader, KIOSK_READER_KEY } from "@/pages/KioskSetup";
import { TerminalPayOverlay } from "@/components/TerminalPayOverlay";
import { brandOpenState } from "@/lib/brandAvailability";
import { useMemberDiscountRule, computeMemberDiscount, memberDiscountLabel } from "@/lib/memberDiscount";
import { formatMoney } from "@/lib/orderUtils";
import { LoyaltyPanel, SlotPicker, CrossSell } from "@/lib/checkoutExtras";
import { ModifierPicker } from "@/lib/ModifierPicker";
import { groupItemsByCategory } from "@/lib/menuGrouping";
import { optimizedImage, optimizedSrcSet } from "@/lib/cloudinaryImage";
import { sortBrandsByAvailability } from "@/lib/brandAvailability";
import { KioskAttract } from "@/components/KioskAttract";
import { KioskIdleGuard } from "@/components/KioskIdleGuard";

const Kiosk = () => {
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeBrand, setActiveBrand] = useState(null); // brand object or null
    const [search, setSearch] = useState("");
    const [lines, setLines] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [showAttract, setShowAttract] = useState(true);
    const [customerName, setCustomerName] = useState("");
    const [notes, setNotes] = useState("");
    const [payMethod, setPayMethod] = useState("card");
    const [placing, setPlacing] = useState(false);
    const [member, setMember] = useState(null);
    const [redeemPoints, setRedeemPoints] = useState(0);
    const [scheduledFor, setScheduledFor] = useState(null);
    const [pickerCtx, setPickerCtx] = useState(null); // { brand, item }

    useEffect(() => {
        // Kiosk-off guard — admin can pause walk-in ordering from Business
        // Settings; when off, redirect customers to the online QR/order flow.
        fetchBusinessSettings()
            .then((s) => {
                if (s && s.kiosk_enabled === false) {
                    window.location.replace("https://order.naamikitchens.com/order");
                    return;
                }
                fetchBrands().then((b) => { setBrands(b); setLoading(false); });
            })
            .catch(() => {
                // If settings fail, fall back to loading brands (fail-open — never
                // stop a customer from ordering because of a network hiccup).
                fetchBrands().then((b) => { setBrands(b); setLoading(false); });
            });
    }, []);

    // scroll to top when entering brand detail
    useEffect(() => {
        if (activeBrand) window.scrollTo({ top: 0, behavior: "instant" });
    }, [activeBrand]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = q
            ? brands.filter((b) =>
                b.name.toLowerCase().includes(q) ||
                b.cuisine.toLowerCase().includes(q) ||
                b.tagline.toLowerCase().includes(q)
            )
            : brands;
        return sortBrandsByAvailability(base);
    }, [brands, search]);

    const subtotal = useMemo(
        () => Math.round(lines.reduce((s, l) => s + l.qty * l.price, 0) * 100) / 100,
        [lines]
    );
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const brandsInCart = new Set(lines.map((l) => l.brand_id)).size;

    const addItem = (brand, item, mods = [], priceOverride = null) => {
        // If item has modifier groups and no mods chosen yet, open picker.
        if ((item.modifier_groups?.length || 0) > 0 && mods.length === 0 && priceOverride === null) {
            setPickerCtx({ brand, item });
            return;
        }
        const modDelta = mods.reduce((s, m) => s + (m.price_delta || 0), 0);
        const unitPrice = priceOverride !== null ? priceOverride : Math.round((item.price + modDelta) * 100) / 100;
        const sig = mods.map((m) => `${m.group_name}:${m.option_name}`).sort().join("|");
        setLines((cur) => {
            const idx = cur.findIndex((l) => l.item_id === item.id && (l._modSig || "") === sig);
            if (idx >= 0) {
                const n = [...cur];
                n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
                return n;
            }
            return [
                ...cur,
                {
                    item_id: item.id, brand_id: brand.id, brand_name: brand.name,
                    brand_token: brand.token, brand_accent: brand.accent,
                    category: item.category || null,
                    name: item.name, price: unitPrice, base_price: item.price, qty: 1,
                    modifiers: mods, _modSig: sig,
                },
            ];
        });
    };

    const changeQty = (itemId, delta, modSig = null) => {
        setLines((cur) =>
            cur.map((l) =>
                l.item_id === itemId && (l._modSig || "") === (modSig || "")
                    ? { ...l, qty: l.qty + delta }
                    : l
            ).filter((l) => l.qty > 0)
        );
    };

    const [reader, setReader] = useState(getKioskReader());
    useEffect(() => {
        kioskDefaultReader().then(({ reader: r }) => {
            if (r) { localStorage.setItem(KIOSK_READER_KEY, JSON.stringify(r)); setReader(r); }
        }).catch((e) => console.error("Kiosk reader lookup failed", e));
    }, []);
    const holdTimer = useRef(null);
    const startHold = () => { holdTimer.current = setTimeout(() => navigate("/kiosk/setup"), 2000); };
    const endHold = () => clearTimeout(holdTimer.current);
    const [terminalPayment, setTerminalPayment] = useState(null);
    const placeOrder = async () => {
        const draft = {
            source: "kiosk", lines,
            customer_name: customerName || null,
            notes: notes || null,
            loyalty_member_id: member?.id || null,
            redeem_points: redeemPoints || 0,
            scheduled_for: scheduledFor || null,
        };
        if (payMethod === "cash") {
            setPlacing(true);
            try {
                const o = await createOrder({ ...draft, pay_method: "cash" });
                setLines([]);
                navigate(`/order/${o.id}`);
            } catch (e) {
                alert(e.response?.data?.detail || "Couldn't place the order. Try again.");
            } finally { setPlacing(false); }
            return;
        }
        if (!reader) {
            alert("This kiosk isn't connected to a card reader yet. Choose cash or order at the counter.");
            return;
        }
        setPlacing(true);
        try {
            const p = await kioskPay(reader.id, { ...draft, pay_method: "card" });
            setTerminalPayment(p);
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Couldn't start the card payment. Try again.");
        } finally { setPlacing(false); }
    };
    const onTerminalPaid = useCallback((orderId) => {
        setTerminalPayment(null);
        setLines([]);
        navigate(`/order/${orderId}`);
    }, [navigate]);

    const resetKiosk = useCallback(() => {
        setLines([]); setActiveBrand(null); setSearch(""); setShowCart(false);
        setCustomerName(""); setNotes(""); setMember(null); setRedeemPoints(0);
        setScheduledFor(null); setPickerCtx(null); setPayMethod("card"); setShowAttract(true);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    if (terminalPayment) {
        return (
            <TerminalPayOverlay
                payment={terminalPayment}
                readerLabel={reader?.label}
                onPaid={onTerminalPaid}
                onClose={() => setTerminalPayment(null)}
            />
        );
    }

    if (showAttract) return <KioskAttract brands={brands} onStart={(b) => { setShowAttract(false); if (b) setActiveBrand(b); }} />;

    const idleGuard = <KioskIdleGuard active onTimeout={resetKiosk} />;

    // Brand detail view
    if (activeBrand) {
        return (
            <>
            <BrandDetail
                brand={activeBrand}
                lines={lines}
                onBack={() => setActiveBrand(null)}
                onAdd={(item) => addItem(activeBrand, item)}
                onInc={(id, modSig) => changeQty(id, 1, modSig)}
                onDec={(id, modSig) => changeQty(id, -1, modSig)}
                cartCount={count}
                cartSubtotal={subtotal}
                brandsInCart={brandsInCart}
                onOpenCart={() => setShowCart(true)}
                showCart={showCart}
                cartProps={{
                    lines, subtotal,
                    onClose: () => setShowCart(false),
                    onInc: (id, modSig) => changeQty(id, 1, modSig),
                    onDec: (id, modSig) => changeQty(id, -1, modSig),
                    onAddCrossSell: (it) => {
                        // it has full brand info from related endpoint
                        addItem(
                            { id: it.brand_id, name: it.brand_name, token: it.brand_token, accent: it.brand_accent },
                            { id: it.item_id, name: it.name, price: it.price }
                        );
                    },
                    customerName, setCustomerName,
                    notes, setNotes,
                    payMethod, setPayMethod,
                    placing, placeOrder,
                    member, setMember,
                    redeemPoints, setRedeemPoints,
                    scheduledFor, setScheduledFor,
                }}
            />
            {idleGuard}
            {pickerCtx && (
                <ModifierPicker
                    item={pickerCtx.item}
                    onCancel={() => setPickerCtx(null)}
                    onConfirm={(mods, totalPrice) => {
                        addItem(pickerCtx.brand, pickerCtx.item, mods, totalPrice);
                        setPickerCtx(null);
                    }}
                />
            )}
            </>
        );
    }

    // Brand grid (default)
    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="kiosk-page">
            {idleGuard}
            <div className="sticky top-0 z-20 bg-[var(--bg)]/95 backdrop-blur border-b border-[var(--border)]">
                <div className="container-nk py-4 flex items-center justify-between gap-4">
                    <button type="button" onClick={() => { setSearch(""); window.scrollTo({ top: 0 }); }}
                        onPointerDown={startHold} onPointerUp={endHold} onPointerLeave={endHold}
                        onContextMenu={(e) => e.preventDefault()}
                        className="flex items-center gap-3 select-none" data-testid="kiosk-home">
                        <div className="font-display text-xl tracking-tight">naami<span className="text-gold">.</span></div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hidden sm:block">
                            // Kiosk
                        </span>
                    </button>
                    <input
                        type="text"
                        placeholder="Search brand or cuisine…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input flex-1 max-w-md"
                        data-testid="kiosk-search"
                    />
                </div>
            </div>

            <div className="container-nk py-12 pb-40">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <p className="eyebrow">// {filtered.length} Brands</p>
                        <h2 className="font-display text-[40px] md:text-[56px] leading-[0.95] mt-3">
                            Pick your <span className="text-gold">craving.</span>
                        </h2>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                        Tap a brand to view full menu
                    </p>
                </div>

                {loading ? (
                    <p className="text-muted">Loading…</p>
                ) : (
                    <div
                        className="grid gap-4"
                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
                    >
                        {sortBrandsByAvailability(filtered).map((b) => {
                            const avail = brandOpenState(b);
                            return (
                                <BrandCard
                                    key={b.id}
                                    brand={b}
                                    closedLabel={avail.available ? null : avail.nextOpenLabel}
                                    inCart={lines.filter((l) => l.brand_id === b.id).reduce((s, l) => s + l.qty, 0)}
                                    onOpen={() => (avail.available ? setActiveBrand(b) : alert(`${b.name} is closed right now.`))}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {count > 0 && !showCart && (
                <button
                    onClick={() => setShowCart(true)}
                    data-testid="kiosk-cart-bar"
                    className="fixed bottom-0 left-0 right-0 bg-[var(--gold)] text-[var(--bg)] py-5 px-8 flex items-center justify-between font-mono uppercase tracking-[0.18em] text-sm z-30 font-bold border-t-2 border-[var(--gold)]"
                >
                    <span>
                        {count} {count === 1 ? "item" : "items"} · {brandsInCart}{" "}
                        {brandsInCart === 1 ? "brand" : "brands"}
                    </span>
                    <span>Review basket · {formatMoney(subtotal)} →</span>
                </button>
            )}

            {showCart && (
                <CartSheet
                    lines={lines} subtotal={subtotal}
                    onClose={() => setShowCart(false)}
                    onInc={(id, modSig) => changeQty(id, 1, modSig)}
                    onDec={(id, modSig) => changeQty(id, -1, modSig)}
                    onAddCrossSell={(it) => {
                        addItem(
                            { id: it.brand_id, name: it.brand_name, token: it.brand_token, accent: it.brand_accent },
                            { id: it.item_id, name: it.name, price: it.price }
                        );
                    }}
                    customerName={customerName} setCustomerName={setCustomerName}
                    notes={notes} setNotes={setNotes}
                    payMethod={payMethod} setPayMethod={setPayMethod}
                    placing={placing} placeOrder={placeOrder}
                    member={member} setMember={setMember}
                    redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                    scheduledFor={scheduledFor} setScheduledFor={setScheduledFor}
                />
            )}
        </div>
    );
};

const BrandCard = ({ brand, inCart, onOpen, closedLabel }) => (
    <article
        className={`brand-card ${closedLabel ? "cursor-not-allowed opacity-50 grayscale" : "cursor-pointer"}`}
        aria-disabled={!!closedLabel}
        style={{ "--brand-color": brand.accent }}
        data-testid={`kiosk-brand-${brand.slug}`}
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
    >
        <div className="photo">
            {brand.image_url && (
                <img src={optimizedImage(brand.image_url, { w: 480 })} srcSet={optimizedSrcSet(brand.image_url, 480)} alt={`${brand.name} signature dish`} loading="lazy" />
            )}
            {brand.halal && <span className="halal-badge">Halal</span>}
            {closedLabel && (
                <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-[3] mx-auto w-fit font-mono font-bold text-[12px] uppercase tracking-[0.2em] px-4 py-2 bg-[var(--bg)] border border-[#ff5246] text-[#ff5246]" data-testid={`kiosk-brand-closed-${brand.slug}`}>
                    {closedLabel}
                </span>
            )}
            {inCart > 0 && (
                <span
                    className="absolute top-3 right-3 z-[2] font-mono font-bold text-[10px] tracking-[0.18em] uppercase px-2 py-1"
                    style={{ background: brand.accent, color: "#0a0a0a" }}
                >
                    {inCart} in cart
                </span>
            )}
            <div className="photo-overlay" aria-hidden />
            <div className="stripe" aria-hidden />
        </div>
        <div className="p-5">
            <div className="flex items-center gap-3.5">
                <div className="logo-box">{brand.token}</div>
                <div className="min-w-0 flex-1">
                    <h3
                        className="font-display text-[24px] leading-tight truncate"
                        style={{ color: brand.accent }}
                    >
                        {brand.name}
                    </h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted truncate mt-0.5">
                        {brand.tagline}
                    </p>
                </div>
            </div>
            <div className="flex items-center justify-between mt-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                    {brand.items?.length || 0} items · {brand.cuisine}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    View menu →
                </span>
            </div>
        </div>
    </article>
);

const BrandDetail = ({
    brand, lines, onBack, onAdd, onInc, onDec,
    cartCount, cartSubtotal, brandsInCart,
    onOpenCart, showCart, cartProps,
}) => {
    const accent = brand.accent;
    return (
        <div
            className="min-h-screen bg-[var(--bg)] text-[var(--text)] pb-40"
            data-testid={`kiosk-brand-detail-${brand.slug}`}
            style={{ "--brand-color": accent }}
        >
            {/* Sticky back nav */}
            <div className="sticky top-0 z-20 bg-[var(--bg)]/95 backdrop-blur border-b border-[var(--border)]">
                <div className="container-nk py-4 flex items-center justify-between gap-4">
                    <button
                        onClick={onBack}
                        className="btn-ghost"
                        data-testid="kiosk-brand-back"
                    >
                        ← All brands
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="logo-box" style={{ width: 36, height: 36, fontSize: 14 }}>
                            {brand.token}
                        </div>
                        <span className="font-display text-[18px] hidden sm:inline" style={{ color: accent }}>
                            {brand.name}
                        </span>
                    </div>
                </div>
            </div>

            {/* Brand hero */}
            <div className="relative">
                {brand.image_url && (
                    <div className="h-72 md:h-96 overflow-hidden">
                        <img
                            src={optimizedImage(brand.image_url, { w: 1200 })}
                            srcSet={optimizedSrcSet(brand.image_url, 1200)}
                            alt={`${brand.name} signature`}
                            className="w-full h-full object-cover opacity-65"
                        />
                    </div>
                )}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(10,10,10,0.45) 0%, rgba(10,10,10,0.2) 40%, var(--bg) 100%)",
                    }}
                />
                <div className="absolute inset-0 flex items-end">
                    <div className="container-nk pb-8 md:pb-12 w-full">
                        <div className="flex items-end gap-5 flex-wrap">
                            <div
                                className="logo-box"
                                style={{ width: 72, height: 72, fontSize: 26 }}
                            >
                                {brand.token}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/80 mb-2">
                                    {brand.cuisine} {brand.halal && "· Halal"}
                                </p>
                                <h1
                                    className="font-display leading-[0.95]"
                                    style={{
                                        color: accent,
                                        fontSize: "clamp(40px, 7vw, 84px)",
                                    }}
                                >
                                    {brand.name}
                                </h1>
                                <p className="font-display text-[18px] md:text-[22px] text-[var(--text)]/85 mt-2">
                                    {brand.tagline}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ background: accent }}
                />
            </div>

            {brand.daily_special && (
                <div className="bg-[var(--gold)] text-[var(--bg)] py-3 px-6 text-center font-mono uppercase tracking-[0.18em] text-[12px] font-bold" data-testid="brand-daily-special-banner">
                    ✦ Today: {brand.daily_special}
                </div>
            )}

            {/* Menu */}
            <div className="container-nk py-12">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <p className="eyebrow" style={{ color: accent }}>// Menu</p>
                        <h2 className="font-display text-[36px] md:text-[48px] leading-[0.95] mt-3">
                            {brand.items?.length || 0} dishes.
                        </h2>
                    </div>
                </div>

                <div className="space-y-10" data-testid="kiosk-menu-grouped">
                    {groupItemsByCategory(brand.items, brand.category_order || [], brand.popular_item_ids || [], brand.category_descriptions || {}).map(({ category, items, isPopular, description }) => (
                        <div key={category} data-testid={`kiosk-cat-${category}`}>
                            <div className="mb-4 pb-2 border-b border-[var(--border)] sticky top-[72px] bg-[var(--bg)]/95 backdrop-blur z-10">
                                <div className="flex items-baseline justify-between">
                                    <h3 className="font-display text-[26px] md:text-[30px] flex items-baseline gap-2" style={{ color: isPopular ? "var(--gold)" : accent }}>
                                        {isPopular && <span className="text-[18px]">★</span>}
                                        {category}
                                    </h3>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                                        {isPopular ? "Last 7 days" : `${items.length} ${items.length === 1 ? "dish" : "dishes"}`}
                                    </span>
                                </div>
                                {description && (
                                    <p className="text-[13px] text-[var(--text)]/65 mt-1 font-light leading-snug">{description}</p>
                                )}
                            </div>
                            <div
                                className="grid gap-5"
                                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
                            >
                                {items.map((item) => {
                                    const qty = lines.filter((l) => l.item_id === item.id).reduce((s, l) => s + l.qty, 0);
                                    const firstLine = lines.find((l) => l.item_id === item.id);
                                    return (
                                        <MenuItemCard
                                            key={item.id}
                                            item={item}
                                            qty={qty}
                                            accent={accent}
                                            onAdd={() => onAdd(item)}
                                            onInc={() => onInc(item.id, firstLine?._modSig)}
                                            onDec={() => onDec(item.id, firstLine?._modSig)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Sticky cart bar */}
            {cartCount > 0 && !showCart && (
                <button
                    onClick={onOpenCart}
                    data-testid="kiosk-cart-bar"
                    className="fixed bottom-0 left-0 right-0 bg-[var(--gold)] text-[var(--bg)] py-5 px-8 flex items-center justify-between font-mono uppercase tracking-[0.18em] text-sm z-30 font-bold border-t-2 border-[var(--gold)]"
                >
                    <span>
                        {cartCount} {cartCount === 1 ? "item" : "items"} · {brandsInCart}{" "}
                        {brandsInCart === 1 ? "brand" : "brands"}
                    </span>
                    <span>Review basket · {formatMoney(cartSubtotal)} →</span>
                </button>
            )}

            {showCart && <CartSheet {...cartProps} />}
        </div>
    );
};

const MenuItemCard = ({ item, qty, accent, onAdd, onInc, onDec }) => {
    const hasMods = (item.modifier_groups?.length || 0) > 0;
    return (
    <article
        className="bg-[var(--surface)] border border-[var(--border)] flex gap-0 hover:border-[var(--gold)] transition-colors overflow-hidden"
        data-testid={`kiosk-item-${item.id}`}
    >
        {item.image_url && (
            <div className="w-32 sm:w-40 shrink-0 relative bg-[var(--surface-elevated)] overflow-hidden">
                <img
                    src={optimizedImage(item.image_url, { w: 160 })}
                    srcSet={optimizedSrcSet(item.image_url, 160)}
                    alt={item.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </div>
        )}
        <div className="flex-1 min-w-0 p-5">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <h3 className="font-display text-[20px] leading-tight">{item.name}</h3>
                {item.popular && (
                    <span
                        className="font-mono text-[9px] uppercase tracking-[0.18em] border px-1.5 py-0.5"
                        style={{ color: accent, borderColor: accent }}
                    >
                        Popular
                    </span>
                )}
            </div>
            <p className="text-[13.5px] text-[var(--text)]/70 font-light leading-relaxed mb-3">
                {item.description}
            </p>
            <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[15px] text-gold">{formatMoney(item.price)}</span>
                {item.calories && (
                    <span className="font-mono text-[11px] text-muted">{item.calories} kcal</span>
                )}
                {item.allergens?.length > 0 && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted" title={item.allergens.join(", ")}>
                        ⚠ {item.allergens.length} allergens
                    </span>
                )}
            </div>
            {qty > 0 && !hasMods ? (
                <div className="flex items-center gap-3">
                    <button
                        onClick={onDec}
                        className="w-12 h-12 border border-[var(--border-strong)] grid place-items-center text-xl hover:border-[var(--gold)]"
                        data-testid={`kiosk-dec-${item.id}`}
                    >−</button>
                    <span className="font-mono text-lg w-10 text-center">{qty}</span>
                    <button
                        onClick={onInc}
                        className="w-12 h-12 border border-[var(--border-strong)] grid place-items-center text-xl hover:border-[var(--gold)]"
                        data-testid={`kiosk-inc-${item.id}`}
                    >+</button>
                    <span className="ml-auto font-mono text-[12px] text-muted">
                        {formatMoney(item.price * qty)}
                    </span>
                </div>
            ) : (
                <button
                    onClick={onAdd}
                    className="btn-primary"
                    data-testid={`kiosk-add-${item.id}`}
                >
                    {hasMods ? (qty > 0 ? `+ Add another (${qty} in cart)` : "+ Customise & add") : "+ Add to basket"}
                </button>
            )}
        </div>
    </article>
    );
};

const CartSheet = ({
    lines, subtotal, onClose, onInc, onDec, onAddCrossSell,
    customerName, setCustomerName, notes, setNotes,
    payMethod, setPayMethod, placing, placeOrder,
    member, setMember, redeemPoints, setRedeemPoints,
    scheduledFor, setScheduledFor,
}) => {
    const grouped = lines.reduce((acc, l) => {
        const key = `${l.brand_id}|${l.brand_name}|${l.brand_token}|${l.brand_accent}`;
        (acc[key] = acc[key] || []).push(l);
        return acc;
    }, {});
    const discount = Math.min((redeemPoints || 0) * 0.05, subtotal);
    const memberRule = useMemberDiscountRule();
    const memberDiscount = computeMemberDiscount(memberRule, { member, hasCode: false, subtotal });
    const [fees, setFees] = useState(null);
    useEffect(() => { fetchBusinessSettings().then(setFees).catch(() => {}); }, []);
    const serviceFee = !fees || subtotal <= 0 ? 0 : Math.min(Number(fees.service_fee_max ?? 2.49) || Infinity,
        Math.max(Number(fees.service_fee_min ?? 0.99), Math.round(subtotal * Number(fees.service_fee_pct ?? 10)) / 100));
    const bagCharge = fees ? Number(fees.bag_charge || 0) : 0;
    const finalTotal = Math.round((Math.max(0, subtotal - Math.min(discount + memberDiscount, subtotal)) + serviceFee + bagCharge) * 100) / 100;

    return (
        <div
            className="fixed inset-0 bg-[#050505] z-40 overflow-y-auto"
            data-testid="kiosk-cart-sheet"
        >
            <div className="min-h-screen flex flex-col">
                <div className="border-b border-[var(--border)] px-6 py-5 flex items-center justify-between sticky top-0 bg-[#050505]">
                    <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold" data-testid="kiosk-cart-close">
                        ← Back to menu
                    </button>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Basket</span>
                </div>
                <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
                    <p className="eyebrow">// Review &amp; Pay</p>
                    <h2 className="font-display text-[44px] md:text-[56px] leading-[0.95] mt-3 mb-10">
                        Your <span className="text-gold">order.</span>
                    </h2>

                    {Object.entries(grouped).map(([key, ls]) => {
                        const [, brandName, token, accent] = key.split("|");
                        return (
                            <div key={key} className="mb-8">
                                <div className="flex items-center gap-3 mb-3 pb-2 border-b border-[var(--border)]">
                                    <div
                                        className="w-7 h-7 grid place-items-center font-display text-xs"
                                        style={{ background: accent, color: "#0a0a0a" }}
                                    >{token}</div>
                                    <span className="font-display text-[18px]" style={{ color: accent }}>
                                        {brandName}
                                    </span>
                                </div>
                                {ls.map((l) => (
                                    <div key={`${l.item_id}-${l._modSig || ""}`} className="py-3 flex items-center gap-4">
                                        <div className="flex-1">
                                            <div>{l.name}</div>
                                            {l.modifiers?.length > 0 && (
                                                <div className="font-mono text-[11px] text-gold/80 leading-tight mt-0.5">
                                                    {l.modifiers.map((m) => m.option_name).join(" · ")}
                                                </div>
                                            )}
                                            <span className="font-mono text-[12px] text-muted">{formatMoney(l.price)}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => onDec(l.item_id, l._modSig)}
                                                className="w-10 h-10 border border-[var(--border-strong)] grid place-items-center"
                                                data-testid={`kiosk-cart-dec-${l.item_id}`}
                                            >−</button>
                                            <span className="font-mono w-7 text-center">{l.qty}</span>
                                            <button
                                                onClick={() => onInc(l.item_id, l._modSig)}
                                                className="w-10 h-10 border border-[var(--border-strong)] grid place-items-center"
                                                data-testid={`kiosk-cart-inc-${l.item_id}`}
                                            >+</button>
                                        </div>
                                        <div className="font-mono w-20 text-right">{formatMoney(l.price * l.qty)}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2 mt-8">
                        Your name (optional)
                    </label>
                    <input
                        className="input mb-5"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Name we'll call out"
                        data-testid="kiosk-name-input"
                    />

                    {/* Cross-sell suggestions */}
                    <CrossSell lines={lines} onAdd={onAddCrossSell} />

                    {/* Loyalty */}
                    <LoyaltyPanel
                        otherDiscount={memberDiscount}
                        member={member} setMember={setMember}
                        redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                        subtotal={subtotal}
                    />

                    {/* Pickup slot */}
                    <SlotPicker scheduledFor={scheduledFor} setScheduledFor={setScheduledFor} />

                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">
                        Allergies / Special instructions
                    </label>
                    <textarea
                        className="input mb-6"
                        rows="3"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Let the kitchen know…"
                        data-testid="kiosk-notes-input"
                    />

                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-3">
                        Payment
                    </label>
                    <div className="mb-8 grid grid-cols-2 gap-3" data-testid="kiosk-pay-options">
                        {[
                            ["card", "Card · Apple Pay · Google Pay", getKioskReader() ? "Pay on the card reader next to this screen." : "Card reader not set up on this kiosk."],
                            ["cash", "Cash", "Order now, pay at the counter when you collect."],
                        ].map(([key, title, sub]) => (
                            <button key={key} type="button" onClick={() => setPayMethod(key)}
                                className={`text-left p-5 border transition-colors ${payMethod === key ? "border-[var(--gold)] bg-[var(--surface-elevated)]" : "border-[var(--border)]"}`}
                                data-testid={`kiosk-pay-${key}`}>
                                <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mb-1 ${payMethod === key ? "text-gold" : "text-muted"}`}>{title}</div>
                                <div className="text-sm text-muted">{sub}</div>
                            </button>
                        ))}
                    </div>

                    <div className="border-t border-[var(--border)] pt-4 space-y-2 mb-6">
                        <div className="flex justify-between font-mono text-[12px] text-muted">
                            <span>Subtotal</span><span>{formatMoney(subtotal)}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="kiosk-discount-row">
                                <span>Loyalty −{redeemPoints} pts</span><span>−{formatMoney(discount)}</span>
                            </div>
                        )}
                        {memberDiscount > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="kiosk-member-discount">
                                <span>{memberDiscountLabel(memberRule)}</span><span>−{formatMoney(memberDiscount)}</span>
                            </div>
                        )}
                        {serviceFee > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="kiosk-service-fee">
                                <span>Service fee</span><span>{formatMoney(serviceFee)}</span>
                            </div>
                        )}
                        {bagCharge > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="kiosk-bag-charge">
                                <span>Bag</span><span>{formatMoney(bagCharge)}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-baseline pt-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Total</span>
                            <span className="font-mono text-2xl text-gold" data-testid="kiosk-total">{formatMoney(finalTotal)}</span>
                        </div>
                    </div>

                    <button
                        onClick={placeOrder}
                        disabled={placing || lines.length === 0}
                        className="btn-primary w-full justify-center text-base py-5"
                        data-testid="kiosk-place-order"
                    >
                        {placing ? (payMethod === "cash" ? "Placing order…" : "Starting payment…") : payMethod === "cash" ? "Place order · pay cash at counter →" : scheduledFor ? "Pay & schedule →" : "Pay on card reader →"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Kiosk;
